import { Check } from '@interhyp/platform-icons';
import classnames from 'classnames';
import React, { useState } from 'react';
import NumberFormat from 'react-number-format';
import { LabelWithoutEventBubbling } from "../../helper/components/LabelWithoutEventBubbling.js";
import { LegacyIconRenderer } from "../../helper/components/LegacyIconRenderer.js";
import { useErrorProps } from "../../helper/hooks/useErrorProps.js";
import { useStableId } from "../../helper/hooks/useStableId.js";
import { IconButton } from "../iconButton/IconButton.js";
import styles from "./Input.module.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function Input({
  id,
  format,
  focus,
  children,
  valid,
  icon,
  iconSmall = false,
  label,
  onFocus,
  onBlur,
  className,
  error,
  small,
  fixedLabel,
  onIconClick,
  disableIconFocus,
  inputRef,
  width,
  textAlign = 'left',
  highlighted,
  startAdornment,
  endAdornment,
  placeholder,
  forceLabelAsPlaceholder = false,
  iconClassName,
  readOnly,
  readOnlyWithStandardStyle,
  ...inputProps
}) {
  const isPlaceholderEmpty = !placeholder;
  const normalizedPlaceholder = isPlaceholderEmpty ? ' ' : placeholder;
  const [focused, setFocused] = useState(false);
  const inputId = useStableId(id);
  const {
    errorMessage,
    errorInputProps
  } = useErrorProps({
    error,
    identifier: inputId
  });
  const classes = classnames(styles.inputContainer, {
    [styles.focused]: typeof focus === 'boolean' ? focus : focused,
    [styles.forceLabelAsPlaceholder]: forceLabelAsPlaceholder || !isPlaceholderEmpty,
    [styles.error]: !!error,
    [styles.disabled]: inputProps.disabled,
    [styles.hidden]: inputProps.hidden,
    [styles.small]: small,
    [styles.highlighted]: highlighted,
    [styles.fixedLabel]: fixedLabel,
    [styles.readOnly]: readOnly,
    [styles.readOnlyWithStandardStyle]: readOnlyWithStandardStyle
  }, className);
  function handleOnFocus(e) {
    if (onFocus) {
      onFocus(e);
    }
    setFocused(true);
  }
  function handleOnBlur(e) {
    if (onBlur) {
      onBlur(e);
    }
    setFocused(false);
  }
  function handleIconClick(e) {
    if (!onIconClick) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    onIconClick(e);
  }
  function renderInput() {
    if (format) {
      return /*#__PURE__*/_jsx(NumberFormat, {
        ...inputProps,
        ...format,
        readOnly: readOnly || readOnlyWithStandardStyle,
        placeholder: normalizedPlaceholder,
        getInputRef: inputRef,
        id: inputId,
        style: {
          textAlign
        },
        onBlur: handleOnBlur,
        onFocus: handleOnFocus,
        className: styles.inputElement
      });
    }
    return /*#__PURE__*/_jsx("input", {
      ...inputProps,
      ...errorInputProps,
      readOnly: readOnly || readOnlyWithStandardStyle,
      placeholder: normalizedPlaceholder,
      style: {
        textAlign
      },
      id: inputId,
      ref: inputRef,
      onBlur: handleOnBlur,
      onFocus: handleOnFocus,
      autoFocus: focus,
      className: classnames(styles.inputElement, startAdornment && styles.withStartAdornment, endAdornment && styles.withEndAdornment)
    });
  }
  return /*#__PURE__*/_jsxs("div", {
    className: classes,
    style: {
      width
    },
    children: [/*#__PURE__*/_jsxs("div", {
      className: classnames(styles.input, startAdornment && styles.withStartAdornment),
      children: [startAdornment && /*#__PURE__*/_jsx("div", {
        className: styles.startAdornment,
        children: startAdornment
      }), renderInput(), /*#__PURE__*/_jsx(LabelWithoutEventBubbling, {
        htmlFor: inputId,
        className: classnames(styles.label, icon && styles.labelWithIconSpace),
        children: /*#__PURE__*/_jsx("span", {
          children: label
        })
      }), endAdornment && /*#__PURE__*/_jsx("div", {
        className: styles.endAdornment,
        children: endAdornment
      }), valid && /*#__PURE__*/_jsx(Check, {
        className: styles.checkmarkIcon
      }), icon && (disableIconFocus || !handleIconClick ? /*#__PURE__*/_jsx("div", {
        role: "img",
        "aria-label": "icon",
        className: classnames(styles.icon, {
          [styles.clickable]: !!onIconClick && !inputProps.disabled
        }),
        onClick: handleIconClick,
        tabIndex: -1,
        children: /*#__PURE__*/_jsx(LegacyIconRenderer, {
          icon: icon,
          className: iconClassName,
          size: iconSmall ? 'SMALL' : 'DEFAULT'
        })
      }) : /*#__PURE__*/_jsx(IconButton, {
        className: styles.iconButton,
        disabled: inputProps.disabled,
        onClick: handleIconClick,
        toolbar: true,
        tertiary: true,
        children: /*#__PURE__*/_jsx(LegacyIconRenderer, {
          icon: icon,
          className: iconClassName,
          size: iconSmall ? 'SMALL' : 'DEFAULT'
        })
      }))]
    }), errorMessage]
  });
}